/**
 * TaskList 筛选字段的中文名 + 值格式化。
 *
 * 用于两个场景:
 * 1. RerunFromQueryConfirmModal —— 用户点「基于当前查询发起任务」时,弹窗里核对
 *    「当前应用了哪些 filter」
 * 2. CreateJobModal「按任务查询」页签 —— 只读展示带过来的 filter 快照
 *
 * 只覆盖跟"筛选场景池"直接相关的 key —— jobId/current/pageSize 这类分页/上下文
 * 字段跳过;compareJobId/compareDiffMode 系列虽然改变列表行的展示,但也影响场景池
 * (影响 sceneCount 就该展示),所以纳入。
 *
 * 值格式化交给 formatFilterValue(),不直接在这里做 —— 有些 filter 值需要运行时
 * 上下文 (statusList / treeNodes / evaluatorName 下拉候选) 才能翻译成中文,组件
 * 侧传 ctx 进来。
 */
import type {
  TaskListParams,
  StatusOption,
  TaskDiffMode,
  CompareConsistency,
  MetricCompare,
  ResponsibilityFilter,
  EvaluatorStatusFilter,
} from '../../apis/simulation';
import { SCENE_REGION_LABELS } from '../../constants/region';
import { SCENE_SET_VERDICT_LABELS } from './sceneSetFilter';
import {
  LABEL_META as BEHAVIOR_LABEL_META,
  STRONG_EVENT_NAMES as BEHAVIOR_STRONG_NAMES,
} from './components/BehaviorCompare/format';

/** 参与「按任务查询」反查的所有 filter key —— 是 TaskListParams 去掉 jobId/current/pageSize 的子集。
 *
 *  `metricSort` 一并排除:它只改**行的先后**,不改行集,也不进 byTaskQuery(后端刻意
 *  不透传),纳入只会让确认弹窗多出一行「指标排序: desc」的噪音。判据与上面 header
 *  一致 —— 只有影响 sceneCount 的才展示。
 */
export type QueryFilterKey = Exclude<
  keyof TaskListParams,
  'jobId' | 'current' | 'pageSize' | 'metricSort'
>;

/** 把 TaskListParams 里跟场景池筛选相关的字段挑出来单独成型,给 confirm/byJob 页签用。 */
export type QueryFilters = Pick<TaskListParams, QueryFilterKey>;

/** 每个 filter key 的中文名。有序 —— 展示时按此顺序渲染,不按对象 key 顺序。 */
export const FILTER_LABELS: Record<QueryFilterKey, string> = {
  sceneName: '场景名',
  taskId: '任务 ID',
  sceneIds: '场景 ID',
  apiCode: 'API Code',
  label: '场景标签',
  mapName: '地图',
  regions: '国家区域',
  compareSceneSetId: '对比场景集',
  sceneSetId: '场景集',
  sceneSetVerdict: '场景集判定',
  latestPerScene: '每场景最新任务',
  treeNodeId: '场景集合(单节点)',
  treeNodeIds: '场景集合',
  // task 状态口径
  status: '任务状态(单)',
  statuses: '任务状态',
  // 评测器
  evaluatorName: '评测器',
  evaluatorStatus: '评测状态',
  evaluatorDiff: '评测器差异口径',
  metricKey: '指标',
  metricCompare: '指标对比方向',
  // 有责/事件/检查项
  responsibility: '有责/无责',
  respEventName: '有无责事件',
  checkName: '检查项',
  checkStatus: '检查项状态',
  // 差异对比
  compareJobId: '对比 Job',
  diffMode: '差异模式',
  diffConsistency: '一致性',
  // 行为对比标签：**会收窄行集**，故必须展示（漏了用户就不知道场景池为什么少了）。
  behaviorLabel: '行为结论',
  behaviorStrong: '行为强事件',
  // 与 diffConsistency 同款:**会收窄行集**,故必须展示(漏一个用户就不知道
  // 场景池为什么少了)。这与 metricSort 恰好相反 —— 那个只改行序,故被排除在外。
  reasonDiff: '原因比对',
  // 原因**类别**筛(当前侧 reason.code)。与 reasonDiff 同款会收窄行集,同样必须展示。
  reasonCode: '原因类别',
  // stats 页「失败原因 / 豁免原因」的种类（fail/exempt）；同样会收窄行集，必须展示。
  reasonKind: '原因种类',
  // 评论
  commentTitle: '评论标题',
  commentKeyword: '评论关键词',
  commentScope: '评论范围',
  hasComment: '有无评论',
  // 对比任务侧评论(daily 对比页专用,须与 compareJobId 同传)
  compareCommentKeyword: '对比任务评论关键词',
  compareCommentScope: '对比任务评论范围',
  compareHasComment: '对比任务有无评论',
  commentCombine: '两侧评论组合方式',
};

/** FILTER_LABELS 的展示顺序 —— 优先级高的排前面,让用户扫读时先看到最常改的。 */
export const FILTER_KEY_ORDER: QueryFilterKey[] = [
  'statuses', 'status',
  'sceneName', 'sceneIds', 'taskId', 'apiCode',
  'label', 'mapName', 'regions', 'treeNodeIds', 'treeNodeId', 'compareSceneSetId', 'sceneSetId', 'sceneSetVerdict', 'latestPerScene',
  'evaluatorName', 'evaluatorStatus', 'evaluatorDiff',
  'metricKey', 'metricCompare',
  'responsibility', 'respEventName', 'checkName', 'checkStatus',
  'compareJobId', 'diffMode', 'diffConsistency', 'behaviorLabel', 'behaviorStrong', 'reasonDiff', 'reasonKind', 'reasonCode',
  'commentTitle', 'commentKeyword', 'commentScope', 'hasComment',
  'compareCommentKeyword', 'compareCommentScope', 'compareHasComment',
  'commentCombine',
];

const DIFF_MODE_LABELS: Record<TaskDiffMode, string> = {
  new_success: '新增通过(仅当前通过)',
  new_fail: '新增失败(仅当前失败)',
  pass_pass: 'Pass · Pass(两侧都通过)',
  pass_fail: 'Pass · Fail(对比通过、当前失败)',
  fail_pass: 'Fail · Pass(对比失败、当前通过)',
  fail_fail: 'Fail · Fail(两侧都失败)',
  all: '全部',
};

/** 行为结论筛选值的中文名；与 BehaviorCompare/format.ts 的 LABEL_META 同源（MISMATCH 为伪标签）。 */
const BEHAVIOR_FILTER_NAMES: Record<string, string> = {
  ...Object.fromEntries(Object.entries(BEHAVIOR_LABEL_META).map(([k, v]) => [k, v.name])),
  MISMATCH: '两侧结论不一致',
};

const CONSISTENCY_LABELS: Record<CompareConsistency, string> = {
  same: '一致',
  diff: '不一致',
  only_current: '仅当前 Job',
};

const METRIC_COMPARE_LABELS: Record<MetricCompare, string> = {
  increase: '当前 > 对比(上升)',
  decrease: '当前 < 对比(下降)',
  equal: '持平',
};

const RESP_LABELS: Record<ResponsibilityFilter, string> = {
  with: '有责',
  without: '无责',
};

const EVAL_STATUS_LABELS: Record<EvaluatorStatusFilter, string> = {
  pass: '通过',
  fail: '不通过',
  error: '异常',
};

const CHECK_STATUS_LABELS: Record<'pass' | 'fail', string> = {
  pass: '通过',
  fail: '不通过',
};

const COMMENT_SCOPE_LABELS: Record<'both' | 'title' | 'body', string> = {
  both: '标题或正文',
  title: '仅标题',
  body: '仅正文',
};

/** 特殊 label 哨兵:未分类场景标签 */
const UNLABELED_LABEL = '__unlabeled__';

/** 运行时上下文 —— 组件把已加载的下拉候选喂进来,让格式化能把 id/code 翻译成中文。 */
export interface FormatCtx {
  /** /task/status 拉到的状态选项;用于把 status/statuses id 翻译成中文名。 */
  statusList?: StatusOption[];
  /** 场景集合节点 id → 中文名映射;用于 treeNodeIds/treeNodeId。可选,不传时降级为原 id。 */
  treeNodeLabels?: Record<number, string>;
}

/** 把 status id 数组翻译成中文名列表(找不到时降级为 String(id))。 */
function statusIdsToNames(ids: number[], statusList?: StatusOption[]): string {
  return ids
    .map((id) => {
      const hit = statusList?.find((s) => Number(s.value) === id);
      return hit?.label ?? String(id);
    })
    .join(', ');
}

function treeNodeIdsToNames(ids: number[], labels?: Record<number, string>): string {
  return ids
    .map((id) => labels?.[id] ?? `#${id}`)
    .join(', ');
}

/** 把某个 filter 值渲染成用户可读的中文字符串。返回 null 表示"这个值应视为空,不渲染"。
 *  空判定:undefined / null / '' / [] / {}。 */
export function formatFilterValue(
  key: QueryFilterKey,
  value: unknown,
  ctx: FormatCtx = {},
): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (Array.isArray(value) && value.length === 0) return null;

  switch (key) {
    case 'statuses':
      return statusIdsToNames(value as number[], ctx.statusList);
    case 'status': {
      // 单值:后端接受 string | number,先转 number 尝试翻译
      const num = typeof value === 'number' ? value : Number(value);
      if (!Number.isNaN(num)) {
        return statusIdsToNames([num], ctx.statusList);
      }
      return String(value);
    }
    case 'sceneIds':
      return (value as number[]).map((n) => `#${n}`).join(', ');
    case 'treeNodeIds':
      return treeNodeIdsToNames(value as number[], ctx.treeNodeLabels);
    case 'sceneSetVerdict':
      return SCENE_SET_VERDICT_LABELS[value as keyof typeof SCENE_SET_VERDICT_LABELS] ?? String(value);
    case 'sceneSetId':
    case 'compareSceneSetId':
      if (value === 0) return '未归属或不可见场景集';
      return ctx.treeNodeLabels?.[value as number] ?? `#${value}`;
    case 'latestPerScene':
      return value ? '是' : '否';
    case 'treeNodeId': {
      const id = value as number;
      return ctx.treeNodeLabels?.[id] ?? `#${id}`;
    }
    case 'label': {
      if (value === UNLABELED_LABEL) return '未分类';
      return String(value);
    }
    // 区域多选：值域固定三点，翻译成中文；未登记的值原样展示（不丢信息）
    case 'regions':
      return (value as string[])
        .map((r) => SCENE_REGION_LABELS[(r || '').toLowerCase()] ?? r)
        .join('、');
    case 'diffMode':
      return DIFF_MODE_LABELS[value as TaskDiffMode] ?? String(value);
    case 'diffConsistency':
      return CONSISTENCY_LABELS[value as CompareConsistency] ?? String(value);
    case 'behaviorLabel':
      return BEHAVIOR_FILTER_NAMES[value as string] ?? String(value);
    case 'behaviorStrong':
      return value === 'ANY' ? '任一强事件' : BEHAVIOR_STRONG_NAMES[value as string] ?? String(value);
    case 'reasonDiff':
      // 与 diffConsistency 同三态、同文案(都复用 CONSISTENCY_LABELS),但为了在确认
      // 弹窗里能一眼看出「这是原因比对而不是场景一致性」,前面冠上「原因」二字。
      // diff_spatial 是原因判定独有的第四态(场景一致性没有),文案不走 CONSISTENCY_LABELS。
      if (value === 'diff_spatial') return '原因同·时空异';
      return `原因${CONSISTENCY_LABELS[value as CompareConsistency] ?? String(value)}`;
    case 'reasonKind':
      return value === 'exempt' ? '豁免原因' : '失败原因';
    case 'reasonCode':
      // 值本身是评估器自描述的 code（如 yellow_light_enter），没有中文映射表 ——
      // 冠上「原因」二字说明这是原因类别，而不是某个评测器名 / 指标 key。
      return `原因·${String(value)}`;
    case 'metricCompare':
      return METRIC_COMPARE_LABELS[value as MetricCompare] ?? String(value);
    case 'responsibility':
      return RESP_LABELS[value as ResponsibilityFilter] ?? String(value);
    case 'evaluatorStatus':
      return EVAL_STATUS_LABELS[value as EvaluatorStatusFilter] ?? String(value);
    case 'checkStatus':
      return CHECK_STATUS_LABELS[value as 'pass' | 'fail'] ?? String(value);
    case 'commentScope':
    case 'compareCommentScope':
      return COMMENT_SCOPE_LABELS[value as 'both' | 'title' | 'body'] ?? String(value);
    case 'hasComment':
    case 'compareHasComment':
      return (value as boolean) ? '有评论' : '无评论';
    case 'commentCombine':
      return value === 'or' ? '两侧任一满足' : '两侧都满足';
    case 'evaluatorDiff':
      return (value as boolean) ? '按评测器差异' : '整体差异';
    case 'taskId':
    case 'compareJobId':
      return `#${value}`;
    default:
      // 其余全部按字符串输出:sceneName / apiCode / mapName / evaluatorName /
      // metricKey / respEventName / checkName / commentTitle / commentKeyword 等
      return String(value);
  }
}

/** 提取 queryParams 里非空 filter,按 FILTER_KEY_ORDER 顺序返回。 */
export interface FilterEntry {
  key: QueryFilterKey;
  label: string;
  valueLabel: string;
}

export function extractNonEmptyFilters(
  filters: QueryFilters,
  ctx: FormatCtx = {},
): FilterEntry[] {
  const out: FilterEntry[] = [];
  for (const key of FILTER_KEY_ORDER) {
    const raw = filters[key];
    const valueLabel = formatFilterValue(key, raw, ctx);
    if (valueLabel === null) continue;
    out.push({ key, label: FILTER_LABELS[key], valueLabel });
  }
  return out;
}

/** 从 TaskListParams 里剥出 filter 子集(丢 jobId/current/pageSize/metricSort —— 见 QueryFilterKey)。 */
export function toQueryFilters(params: TaskListParams): QueryFilters {
  const { jobId, current, pageSize, metricSort, ...rest } = params;
  void jobId; void current; void pageSize; void metricSort;
  return rest;
}
